import { Header, RowData, Table, flexRender } from '@tanstack/react-table';
import { Flex } from 'antd';

import { ArrowDown } from '../../icons';
import { Button } from '../Button/Button';

function getSortButtonLabel<T extends RowData>(header: Header<T, unknown>) {
  const columnHeader = header.column.columnDef.header;
  const columnLabel =
    typeof columnHeader === 'string' ? columnHeader : header.column.id;
  const sortDirection = header.column.getIsSorted();
  if (sortDirection === 'asc') {
    return `Sort by ${columnLabel}, currently sorted ascending`;
  }
  if (sortDirection === 'desc') {
    return `Sort by ${columnLabel}, currently sorted descending`;
  }
  return `Sort by ${columnLabel}`;
}

export interface TableHeaderRowsProps<T extends RowData> {
  table: Table<T>;
  highLightColumnsById?: string[];
  widthProperty?: 'minWidth' | 'maxWidth';
  sortIconClassName?: string;
}

export function TableHeaderRows<T extends RowData>({
  table,
  highLightColumnsById = [],
  widthProperty = 'minWidth',
  sortIconClassName,
}: Readonly<TableHeaderRowsProps<T>>) {
  return table.getHeaderGroups().map((headerGroup) => (
    <tr className="tr" style={{ position: 'relative' }} key={headerGroup.id}>
      {headerGroup.headers.map((header) => (
        <th
          key={header.id}
          scope="col"
          className={`th ${
            highLightColumnsById.includes(header.id) ? 'highlight-header' : ''
          }`}
          colSpan={header.colSpan}
          style={{
            [widthProperty]: header.getSize(),
            position: 'relative',
          }}
        >
          {header.isPlaceholder ? null : (
            <Flex>
              <div className="table-title">
                {flexRender(
                  header.column.columnDef.header,
                  header.getContext(),
                )}
              </div>
              {header.column.getCanSort() ? (
                <Button
                  className={`sort-button ${header.column.getIsSorted()}`}
                  intent="icon"
                  aria-label={getSortButtonLabel(header)}
                  onClick={() => header.column.toggleSorting()}
                >
                  <ArrowDown className={sortIconClassName} />
                </Button>
              ) : null}
            </Flex>
          )}

          {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions -- TanStack resizing requires mouse and touch drag handlers */}
          <div
            id={header.id}
            hidden={!header.column.getCanResize()}
            onMouseDown={header.getResizeHandler()}
            onTouchStart={header.getResizeHandler()}
            className={`resizer ${
              header.column.getIsResizing() ? 'isResizing' : ''
            }`}
            style={{
              transform: header.column.getIsResizing()
                ? `translateX(${table.getState().columnSizingInfo.deltaOffset}px)`
                : '',
            }}
          />
        </th>
      ))}
    </tr>
  ));
}
